import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { request } from "utils/request";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { getUserToken, setUserToken } from "utils/helperFunctions";
import type { RootState } from "utils/redux/store";
import {
  ChildCompany,
  HierarchyState,
  ParentCompany,
  SwitchCompanyResponse,
} from "./types";

const ORIGINAL_TOKEN_KEY = "originalCritToken";
const CHILD_COMPANY_NAME_KEY = "hierarchyChildCompanyName";
const PARENT_COMPANY_NAME_KEY = "hierarchyParentCompanyName";
const EXPLICIT_SWITCH_KEY = "hierarchyExplicitSwitch";

export const getOriginalToken = () => localStorage.getItem(ORIGINAL_TOKEN_KEY);
export const setOriginalToken = (token: string) =>
  localStorage.setItem(ORIGINAL_TOKEN_KEY, token);
export const clearOriginalToken = () =>
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);

export const markExplicitHierarchySwitch = () =>
  sessionStorage.setItem(EXPLICIT_SWITCH_KEY, "true");

export const consumeExplicitHierarchySwitch = (): boolean => {
  const isExplicitSwitch = sessionStorage.getItem(EXPLICIT_SWITCH_KEY) === "true";
  sessionStorage.removeItem(EXPLICIT_SWITCH_KEY);
  return isExplicitSwitch;
};

export const restoreParentTokenOnRefresh = (): boolean => {
  const originalToken = getOriginalToken();
  if (!originalToken) return false;

  setUserToken(originalToken);
  clearOriginalToken();
  localStorage.removeItem(CHILD_COMPANY_NAME_KEY);
  localStorage.removeItem(PARENT_COMPANY_NAME_KEY);
  return true;
};

const initialState: HierarchyState = {
  childCompanies: [],
  isInHierarchyContext: false,
  accessLevel: null,
  originalCompanyAccountId: null,
  originalUserAccountId: null,
  originalCompanyName: null,
  activeChildCompanyName: null,
  getChildCompaniesStatus: "idle",
  switchCompanyStatus: "idle",
  parentCompanies: [],
  getParentCompaniesStatus: "idle",
};

export const getChildCompanies = createAsyncThunk(
  "hierarchy/getChildCompanies",
  async () => {
    try {
      const url = `${REACT_APP_API_URL}/talentInsights/hierarchy/companies`;
      return (await request(url)) as ChildCompany[];
    } catch {
      return [] as ChildCompany[];
    }
  },
  {
    condition: (_, { getState }) => {
      const { hierarchy } = getState() as RootState;
      return hierarchy.getChildCompaniesStatus === "idle";
    },
  }
);

export const getParentCompanies = createAsyncThunk(
  "hierarchy/getParentCompanies",
  async () => {
    try {
      const url = `${REACT_APP_API_URL}/talentInsights/hierarchy/parent`;
      const response = (await request(url)) as { parents: ParentCompany[] };
      return response.parents;
    } catch {
      return [] as ParentCompany[];
    }
  },
  {
    condition: (_, { getState }) => {
      const { hierarchy } = getState() as RootState;
      return hierarchy.getParentCompaniesStatus === "idle";
    },
  }
);

export const switchToChildCompany = createAsyncThunk(
  "hierarchy/switchToChildCompany",
  async (
    {
      childCompanyAccountId,
      childCompanyName,
      originalCompanyName,
    }: {
      childCompanyAccountId: number;
      childCompanyName: string;
      // The active company's name when switching out of the parent. Inside a child the app's
      // company info describes the child, so the parent's name is remembered from the first switch.
      originalCompanyName?: string;
    },
    { rejectWithValue }
  ) => {
    const tokenBeforeSwitch = getUserToken();
    try {
      const url = `${REACT_APP_API_URL}/talentInsights/hierarchy/switchCompany`;

      // If already in a child company context, restore the parent token before
      // calling switchCompany — the backend needs a parent-company JWT to issue
      // a new hierarchy token.
      const existingOriginalToken = getOriginalToken();
      if (existingOriginalToken) {
        setUserToken(existingOriginalToken);
      }

      const response = (await request(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ childCompanyAccountId }),
      })) as SwitchCompanyResponse;

      const currentToken = getUserToken();
      if (currentToken && !getOriginalToken()) {
        setOriginalToken(currentToken);
      }
      markExplicitHierarchySwitch();
      localStorage.setItem(CHILD_COMPANY_NAME_KEY, childCompanyName);
      const parentCompanyName =
        localStorage.getItem(PARENT_COMPANY_NAME_KEY) ?? originalCompanyName ?? null;
      if (parentCompanyName) {
        localStorage.setItem(PARENT_COMPANY_NAME_KEY, parentCompanyName);
      }
      setUserToken(response.token);

      return {
        originalCompanyName: parentCompanyName,
        accessLevel: response.accessLevel,
        originalCompanyAccountId: response.originalCompanyAccountId,
        originalUserAccountId: response.originalUserAccountId,
        activeChildCompanyName: childCompanyName,
      };
    } catch (e) {
      // The parent token may have been swapped in above; put back the company the user was in.
      if (tokenBeforeSwitch) {
        setUserToken(tokenBeforeSwitch);
      }
      return rejectWithValue("Failed to switch company");
    }
  }
);

export const switchBackToOriginalCompany = createAsyncThunk(
  "hierarchy/switchBackToOriginalCompany",
  () => {
    const originalToken = getOriginalToken();
    if (originalToken) {
      setUserToken(originalToken);
      clearOriginalToken();
    }
    localStorage.removeItem(CHILD_COMPANY_NAME_KEY);
    localStorage.removeItem(PARENT_COMPANY_NAME_KEY);
  }
);

export const hierarchySlice = createSlice({
  name: "hierarchy",
  initialState,
  reducers: {
    restoreHierarchyContext(
      state,
      action: PayloadAction<{
        accessLevel: "read" | "admin";
        originalCompanyAccountId: number;
        originalUserAccountId?: number;
      }>
    ) {
      state.isInHierarchyContext = true;
      state.accessLevel = action.payload.accessLevel;
      state.originalCompanyAccountId = action.payload.originalCompanyAccountId;
      state.originalUserAccountId = action.payload.originalUserAccountId ?? null;
      state.activeChildCompanyName =
        localStorage.getItem(CHILD_COMPANY_NAME_KEY) ?? null;
      state.originalCompanyName =
        localStorage.getItem(PARENT_COMPANY_NAME_KEY) ?? null;
    },
    resetHierarchyContext(state) {
      state.isInHierarchyContext = false;
      state.accessLevel = null;
      state.originalCompanyAccountId = null;
      state.originalCompanyName = null;
      state.activeChildCompanyName = null;
    },
    resetGetChildCompaniesStatus(state) {
      state.getChildCompaniesStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChildCompanies.pending, (state) => {
        state.getChildCompaniesStatus = "loading";
      })
      .addCase(getChildCompanies.fulfilled, (state, action) => {
        state.getChildCompaniesStatus = "succeeded";
        state.childCompanies = action.payload;
      })
      .addCase(getChildCompanies.rejected, (state) => {
        state.getChildCompaniesStatus = "failed";
      })
      .addCase(getParentCompanies.pending, (state) => {
        state.getParentCompaniesStatus = "loading";
      })
      .addCase(getParentCompanies.fulfilled, (state, action) => {
        state.getParentCompaniesStatus = "succeeded";
        state.parentCompanies = action.payload;
      })
      .addCase(getParentCompanies.rejected, (state) => {
        state.getParentCompaniesStatus = "failed";
      })
      .addCase(switchToChildCompany.pending, (state) => {
        state.switchCompanyStatus = "loading";
      })
      .addCase(switchToChildCompany.fulfilled, (state, action) => {
        state.switchCompanyStatus = "succeeded";
        state.isInHierarchyContext = true;
        state.accessLevel = action.payload.accessLevel;
        state.originalCompanyAccountId = action.payload.originalCompanyAccountId;
        state.originalUserAccountId = action.payload.originalUserAccountId ?? null;
        state.originalCompanyName = action.payload.originalCompanyName;
        state.activeChildCompanyName = action.payload.activeChildCompanyName;
      })
      .addCase(switchToChildCompany.rejected, (state) => {
        state.switchCompanyStatus = "failed";
      })
      .addCase(switchBackToOriginalCompany.fulfilled, (state) => {
        state.isInHierarchyContext = false;
        state.accessLevel = null;
        state.originalCompanyAccountId = null;
        state.originalUserAccountId = null;
        state.originalCompanyName = null;
        state.activeChildCompanyName = null;
        state.switchCompanyStatus = "idle";
      });
  },
});

export const {
  restoreHierarchyContext,
  resetHierarchyContext,
  resetGetChildCompaniesStatus,
} = hierarchySlice.actions;

export const selectChildCompanies = (state: RootState) =>
  state.hierarchy.childCompanies;
export const selectIsInHierarchyContext = (state: RootState) =>
  state.hierarchy.isInHierarchyContext;
export const selectHierarchyAccessLevel = (state: RootState) =>
  state.hierarchy.accessLevel;
export const selectOriginalCompanyAccountId = (state: RootState) =>
  state.hierarchy.originalCompanyAccountId;
export const selectOriginalUserAccountId = (state: RootState) =>
  state.hierarchy.originalUserAccountId;
export const selectOriginalCompanyName = (state: RootState) =>
  state.hierarchy.originalCompanyName;
export const selectActiveChildCompanyName = (state: RootState) =>
  state.hierarchy.activeChildCompanyName;
export const selectGetChildCompaniesStatus = (state: RootState) =>
  state.hierarchy.getChildCompaniesStatus;
export const selectSwitchCompanyStatus = (state: RootState) =>
  state.hierarchy.switchCompanyStatus;
export const selectIsHierarchyReadOnly = (state: RootState) =>
  state.hierarchy.isInHierarchyContext &&
  state.hierarchy.accessLevel === "read";
export const selectParentCompanies = (state: RootState) =>
  state.hierarchy.parentCompanies;
export const selectGetParentCompaniesStatus = (state: RootState) =>
  state.hierarchy.getParentCompaniesStatus;

export default hierarchySlice.reducer;
